import { useEffect, useMemo, useState } from 'react';
import { XMarkIcon, InboxArrowDownIcon } from '@heroicons/react/24/outline';
import {
  useExpectedDeliveries, usePoForReceiving, useReceipts, useReceipt,
  usePodPhotos,
  useCreateReceipt, useSaveReceiptLines, useConfirmReceipt, useStorerooms,
} from '../hooks/useReceiving';

const fmtDate = (v) => (v ? new Date(v).toLocaleDateString('en-GB') : '—');
const num = (v) => Number(v || 0).toLocaleString('en-GB', { maximumFractionDigits: 3 });


const PO_PILL = {
  sent: 'bg-sky-100 text-sky-800', approved: 'bg-sky-100 text-sky-800',
  part_received: 'bg-amber-100 text-amber-800', received: 'bg-emerald-100 text-emerald-800',
};
const GRN_PILL = { draft: 'bg-amber-100 text-amber-800', confirmed: 'bg-emerald-100 text-emerald-800', cancelled: 'bg-gray-100 text-gray-500' };
const label = (s) => String(s || '').replace('_', ' ');

function Pill({ status, map }) {
  return <span className={`rounded-full px-2.5 py-1 text-xs font-medium ${map[status] || 'bg-gray-100 text-gray-600'}`}>{label(status)}</span>;
}

// A shared editable line table used by both the new-receipt and the finish-draft
// flows. `lines` carry {key, item_code, name, ordered, outstanding} + edit state.
// Counted goods, so whole numbers only -- the field used to accept 2.5 eyebolts.
// Anything typed is coerced on the way in rather than validated on the way out.
const whole = (v) => {
  const t = String(v ?? '').replace(/[^0-9]/g, '');
  return t === '' ? '' : String(parseInt(t, 10));
};

function QtyCell({ readOnly, value, onChange, tone }) {
  const ring = tone === 'amber' ? 'border-amber-300 focus:border-amber-500'
    : tone === 'red' ? 'border-red-300 focus:border-red-500' : 'border-gray-300';
  const text = tone === 'amber' ? 'text-amber-700' : tone === 'red' ? 'text-red-700' : 'text-gray-800';
  return (
    <td className="px-3 py-2 text-right">
      {readOnly ? (
        <span className={`tabular-nums font-semibold ${Number(value) ? text : 'text-gray-300'}`}>{Number(value) || 0}</span>
      ) : (
        <input
          type="text" inputMode="numeric" value={value ?? ''}
          onChange={(ev) => onChange(whole(ev.target.value))}
          placeholder="0"
          className={`w-20 rounded-md border px-2 py-1 text-right text-sm tabular-nums ${ring}`}
        />
      )}
    </td>
  );
}

function LineTable({ rows, edits, setEdit, readOnly }) {
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
          <tr>
            <th className="px-3 py-2">Item</th>
            <th className="px-3 py-2 text-right">Ordered</th>
            <th className="px-3 py-2 text-right">Outstanding</th>
            <th className="px-3 py-2 text-right">Received</th>
            <th className="px-3 py-2 text-right">Damaged</th>
            <th className="px-3 py-2 text-right">Rejected</th>
            <th className="px-3 py-2">Note</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((r) => {
            const e = edits[r.key] || {};
            return (
              <tr key={r.key}>
                <td className="px-3 py-2">
                  <div className="font-medium text-gray-800">{r.name}</div>
                  <div className="font-mono text-xs text-gray-500">{r.item_code}</div>
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-gray-500">{num(r.ordered)}</td>
                <td className="px-3 py-2 text-right tabular-nums text-gray-600">{num(r.outstanding)}</td>
                {/* Three separate counts rather than one quantity and a
                    condition: a single delivery line can be part good, part
                    smashed and part the wrong item all at once, and the old
                    shape forced the storeman to pick one story for the lot. */}
                <QtyCell readOnly={readOnly} value={e.qty} onChange={(v) => setEdit(r.key, { qty: v })} />
                <QtyCell readOnly={readOnly} value={e.damaged} tone="amber" onChange={(v) => setEdit(r.key, { damaged: v })} />
                <QtyCell readOnly={readOnly} value={e.rejected} tone="red" onChange={(v) => setEdit(r.key, { rejected: v })} />
                <td className="px-3 py-2">
                  {readOnly ? <span className="text-gray-500">{e.note || '—'}</span> : (
                    <input type="text" value={e.note || ''} onChange={(ev) => setEdit(r.key, { note: ev.target.value })} placeholder="Discrepancy…" className="w-40 rounded-md border border-gray-300 px-2 py-1 text-sm" />
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function Drawer({ title, subtitle, children, onClose }) {
  return (
    <div className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4" onClick={onClose}>
      <div className="max-h-[90vh] w-full max-w-4xl overflow-y-auto rounded-lg bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="mb-4 flex items-start justify-between">
          <div><h2 className="text-lg font-bold text-gray-900">{title}</h2>{subtitle && <p className="text-sm text-gray-500">{subtitle}</p>}</div>
          <button type="button" onClick={onClose} className="rounded p-1 text-gray-400 hover:bg-gray-100"><XMarkIcon className="h-5 w-5" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}

// New delivery against a PO — creates + confirms in one go (no lingering draft),
// or "Save as draft" to finish later.
function ReceiveDrawer({ po, onClose }) {
  const { data, isLoading } = usePoForReceiving(po.id);
  const { data: storerooms } = useStorerooms();
  const create = useCreateReceipt();
  const saveLines = useSaveReceiptLines();
  const confirm = useConfirmReceipt();

  const [storeroomId, setStoreroomId] = useState('');
  const [deliveryNote, setDeliveryNote] = useState('');
  const [note, setNote] = useState('');
  const [edits, setEdits] = useState({});
  const [flash, setFlash] = useState(null);
  const [busy, setBusy] = useState(false);

  const rows = useMemo(() => (data?.lines || []).filter((l) => Number(l.outstanding) > 0)
    .map((l) => ({ key: l.id, poLineId: l.id, item_code: l.item_code, name: l.name, ordered: l.quantity, outstanding: l.outstanding })), [data]);

  useEffect(() => {
    const init = {};
    rows.forEach((r) => { init[r.key] = { qty: String(r.outstanding), condition: 'ok', note: '' }; });
    setEdits(init);
  }, [rows.length]); // eslint-disable-line react-hooks/exhaustive-deps

  const setEdit = (key, patch) => setEdits((s) => ({ ...s, [key]: { ...s[key], ...patch } }));

  const buildLines = (draftLines) => draftLines.map((dl) => {
    const e = edits[dl.purchase_order_line_id] || {};
    return {
      id: dl.id,
      qty_received: Number(e.qty || 0),
      qty_damaged: Number(e.damaged || 0),
      qty_rejected: Number(e.rejected || 0),
      reject_reason: e.rejected ? (e.note || '') : '',
      // Kept for the existing receipt view; the three counts are the record now.
      line_condition: Number(e.rejected) ? 'damaged' : Number(e.damaged) ? 'damaged' : (e.condition || 'ok'),
      discrepancy_note: e.note || '',
    };
  });

  const run = async (confirmIt) => {
    setFlash(null);
    if (confirmIt && !storeroomId) { setFlash({ ok: false, msg: 'Choose the storeroom the goods were received into.' }); return; }
    const anyQty = Object.values(edits).some((e) => Number(e.qty || 0) > 0);
    if (!anyQty) { setFlash({ ok: false, msg: 'Enter a received quantity on at least one line.' }); return; }
    setBusy(true);
    try {
      const draft = await create.mutateAsync({ po_id: po.id, storeroom_id: storeroomId || null, delivery_note_ref: deliveryNote || null, note: note || null });
      await saveLines.mutateAsync({ id: draft.id, lines: buildLines(draft.lines) });
      if (confirmIt) {
        const r = await confirm.mutateAsync({ id: draft.id, storeroom_id: storeroomId, note: note || null });
        setFlash({ ok: true, msg: `${r.grn_number} received — PO is now ${label(r.po_status)}.` });
        setTimeout(onClose, 900);
      } else {
        setFlash({ ok: true, msg: `Saved as draft ${draft.grn_number}. Finish it from Receipt history.` });
        setTimeout(onClose, 900);
      }
    } catch (e) {
      setFlash({ ok: false, msg: e?.response?.data?.error || 'Could not save the receipt.' });
    } finally { setBusy(false); }
  };

  return (
    <Drawer title={`Receive ${po.po_number}`} subtitle={`${po.supplier_name}${po.xero_po_number ? ` · Xero ${po.xero_po_number}` : ''}`} onClose={onClose}>
      {isLoading ? <div className="text-sm text-gray-400">Loading…</div> : (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <label className="block">
              <span className="mb-1 block text-xs font-semibold text-gray-500">Received into storeroom *</span>
              <select value={storeroomId} onChange={(e) => setStoreroomId(e.target.value)} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm">
                <option value="">— choose —</option>
                {(storerooms || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
              </select>
            </label>
            <label className="block">
              <span className="mb-1 block text-xs font-semibold text-gray-500">Delivery note ref</span>
              <input type="text" value={deliveryNote} onChange={(e) => setDeliveryNote(e.target.value)} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" />
            </label>
          </div>

          {rows.length === 0 ? <p className="text-sm text-gray-500">Nothing left to receive on this PO.</p> : (
            <LineTable rows={rows} edits={edits} setEdit={setEdit} />
          )}

          <label className="block">
            <span className="mb-1 block text-xs font-semibold text-gray-500">Note (optional)</span>
            <input type="text" value={note} onChange={(e) => setNote(e.target.value)} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm" />
          </label>

          {flash && <div className={`rounded-lg px-4 py-2.5 text-sm font-medium ${flash.ok ? 'bg-emerald-50 text-emerald-800' : 'bg-rose-50 text-rose-700'}`}>{flash.msg}</div>}

          <div className="flex items-center gap-2">
            <button type="button" disabled={busy || !rows.length} onClick={() => run(true)} className="flex-1 rounded-md bg-brand py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50">
              {busy ? 'Saving…' : 'Confirm receipt'}
            </button>
            <button type="button" disabled={busy || !rows.length} onClick={() => run(false)} className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50">Save as draft</button>
          </div>
        </div>
      )}
    </Drawer>
  );
}

// View a receipt; a draft can be finished (counts + confirm) from here.
// The delivery notes the storeman photographed, for whoever is arguing a short
// or damaged delivery with a supplier. Download matters as much as viewing:
// the note usually has to be attached to an email or a credit-note claim.
function PodPhotos({ grnId, grnNumber }) {
  const { data, isLoading, isError } = usePodPhotos(grnId);
  const photos = data?.photos || (data?.photo ? [{ id: 0, photo: data.photo }] : []);

  // Data URLs already hold the bytes, so a download needs no second request.
  const filename = (i, src) => {
    const ext = /^data:image\/png/.test(src) ? 'png' : 'jpg';
    return `${grnNumber || 'delivery-note'}-${i + 1}.${ext}`;
  };

  if (isLoading) return <p className="mt-4 text-sm text-gray-400">Loading delivery notes…</p>;
  if (isError || !photos.length) {
    return (
      <p className="mt-4 rounded-md border border-dashed border-gray-200 px-3 py-4 text-center text-sm text-gray-400">
        No delivery note was photographed for this receipt.
      </p>
    );
  }
  return (
    <div className="mt-5">
      <div className="mb-2 flex items-baseline justify-between">
        <h3 className="text-xs font-bold uppercase tracking-wide text-gray-500">
          Delivery notes ({photos.length})
        </h3>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        {photos.map((ph, i) => (
          <div key={ph.id != null ? ph.id : i} className="rounded-lg border border-gray-200 p-2">
            {/* Opens full size in a new tab; the thumbnail is unreadable for a
                handwritten note. */}
            <a href={ph.photo} target="_blank" rel="noreferrer">
              <img src={ph.photo} alt={`Delivery note ${i + 1} for ${grnNumber}`}
                className="h-40 w-full rounded object-cover" />
            </a>
            <a href={ph.photo} download={filename(i, ph.photo)}
              className="mt-2 block text-center text-xs font-semibold text-brand hover:underline">
              Download
            </a>
          </div>
        ))}
      </div>
    </div>
  );
}

function ReceiptDrawer({ grnId, onClose }) {
  const { data, isLoading } = useReceipt(grnId);
  const { data: storerooms } = useStorerooms();
  const saveLines = useSaveReceiptLines();
  const confirm = useConfirmReceipt();
  const [storeroomId, setStoreroomId] = useState('');
  const [edits, setEdits] = useState({});
  const [flash, setFlash] = useState(null);
  const [busy, setBusy] = useState(false);
  const isDraft = data && data.status === 'draft';

  const rows = useMemo(() => (data?.lines || []).map((l) => ({
    key: l.id, item_code: l.item_code, name: l.name, ordered: l.qty_ordered, outstanding: l.qty_ordered,
  })), [data]);

  useEffect(() => {
    if (!data) return;
    setStoreroomId(data.storeroom_id ? String(data.storeroom_id) : '');
    const init = {};
    (data.lines || []).forEach((l) => {
      init[l.id] = {
        qty: String(Number(l.qty_received) || 0),
        damaged: String(Number(l.qty_damaged) || 0),
        rejected: String(Number(l.qty_rejected) || 0),
        condition: l.line_condition || 'ok',
        note: l.discrepancy_note || l.reject_reason || '',
      };
    });
    setEdits(init);
  }, [data]);

  const setEdit = (key, patch) => setEdits((s) => ({ ...s, [key]: { ...s[key], ...patch } }));

  const doConfirm = async () => {
    setFlash(null);
    if (!storeroomId) { setFlash({ ok: false, msg: 'Choose the storeroom.' }); return; }
    setBusy(true);
    try {
      await saveLines.mutateAsync({ id: grnId, lines: (data.lines || []).map((l) => ({
        id: l.id,
        qty_received: Number(edits[l.id]?.qty || 0),
        qty_damaged: Number(edits[l.id]?.damaged || 0),
        qty_rejected: Number(edits[l.id]?.rejected || 0),
        reject_reason: edits[l.id]?.rejected ? (edits[l.id]?.note || '') : '',
        line_condition: edits[l.id]?.condition || 'ok',
        discrepancy_note: edits[l.id]?.note || '',
      })) });
      const r = await confirm.mutateAsync({ id: grnId, storeroom_id: storeroomId });
      setFlash({ ok: true, msg: `${r.grn_number} received — PO is now ${label(r.po_status)}.` });
      setTimeout(onClose, 900);
    } catch (e) { setFlash({ ok: false, msg: e?.response?.data?.error || 'Could not confirm.' }); }
    finally { setBusy(false); }
  };

  if (isLoading || !data) return <Drawer title="Receipt" onClose={onClose}><div className="text-sm text-gray-400">Loading…</div></Drawer>;

  return (
    <Drawer title={data.grn_number} subtitle={`${data.po_number} · ${data.supplier_name}`} onClose={onClose}>
      <div className="mb-3 flex items-center gap-3">
        <Pill status={data.status} map={GRN_PILL} />
        {data.received_by_name && <span className="text-xs text-gray-500">by {data.received_by_name}</span>}
        {data.confirmed_at && <span className="text-xs text-gray-500">on {fmtDate(data.confirmed_at)}</span>}
        {data.delivery_note_ref && <span className="text-xs text-gray-500">DN {data.delivery_note_ref}</span>}
      </div>

      {isDraft ? (
        <label className="mb-3 block">
          <span className="mb-1 block text-xs font-semibold text-gray-500">Received into storeroom *</span>
          <select value={storeroomId} onChange={(e) => setStoreroomId(e.target.value)} className="w-full max-w-xs rounded-md border border-gray-300 px-3 py-2 text-sm">
            <option value="">— choose —</option>
            {(storerooms || []).map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>
        </label>
      ) : (
        <p className="mb-3 text-sm text-gray-600">Received into <span className="font-medium">{data.storeroom_name || '—'}</span></p>
      )}

      <LineTable rows={rows} edits={edits} setEdit={setEdit} readOnly={!isDraft} />

      <PodPhotos grnId={grnId} grnNumber={data.grn_number} />

      {flash && <div className={`mt-4 rounded-lg px-4 py-2.5 text-sm font-medium ${flash.ok ? 'bg-emerald-50 text-emerald-800' : 'bg-rose-50 text-rose-700'}`}>{flash.msg}</div>}

      {isDraft && (
        <button type="button" disabled={busy} onClick={doConfirm} className="mt-4 w-full rounded-md bg-brand py-2 text-sm font-semibold text-white hover:opacity-90 disabled:opacity-50">
          {busy ? 'Confirming…' : 'Confirm receipt'}
        </button>
      )}
    </Drawer>
  );
}

function ExpectedTab({ onReceive }) {
  const { data, isLoading, isError } = useExpectedDeliveries();
  const orders = data || [];
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
          <tr>
            <th className="px-4 py-2.5">PO</th><th className="px-4 py-2.5">Supplier</th>
            <th className="px-4 py-2.5 text-right">Ordered</th><th className="px-4 py-2.5 text-right">Received</th><th className="px-4 py-2.5 text-right">Outstanding</th>
            <th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5">Raised</th><th className="px-4 py-2.5"></th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {isLoading && <tr><td colSpan={8} className="px-4 py-6 text-center text-gray-400">Loading…</td></tr>}
          {isError && <tr><td colSpan={8} className="px-4 py-6 text-center text-rose-500">Could not load expected deliveries.</td></tr>}
          {!isLoading && !isError && orders.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">No open purchase orders — nothing to receive right now.</td></tr>}
          {orders.map((o) => (
            <tr key={o.id} className="hover:bg-brand-light/40">
              <td className="whitespace-nowrap px-4 py-2.5">
                <div className="font-medium text-gray-800">{o.po_number}</div>
                {o.xero_po_number && <div className="text-xs text-gray-400">Xero {o.xero_po_number}</div>}
              </td>
              <td className="px-4 py-2.5 text-sm text-gray-700">{o.supplier_name}</td>
              <td className="px-4 py-2.5 text-right tabular-nums text-gray-500">{num(o.qty_ordered)}</td>
              <td className="px-4 py-2.5 text-right tabular-nums text-gray-500">{num(o.qty_received)}</td>
              <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-gray-800">{num(o.qty_outstanding)}</td>
              <td className="px-4 py-2.5"><Pill status={o.status} map={PO_PILL} /></td>
              <td className="whitespace-nowrap px-4 py-2.5 text-sm text-gray-500">{fmtDate(o.created_at)}</td>
              <td className="px-4 py-2.5 text-right">
                <button type="button" onClick={() => onReceive(o)} className="inline-flex items-center gap-1.5 rounded-md bg-brand px-3 py-1.5 text-sm font-semibold text-white hover:opacity-90">
                  <InboxArrowDownIcon className="h-4 w-4" /> Receive
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function HistoryTab({ onOpen }) {
  const { data, isLoading } = useReceipts();
  const rows = data || [];
  return (
    <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
          <tr>
            <th className="px-4 py-2.5">GRN</th><th className="px-4 py-2.5">PO</th><th className="px-4 py-2.5">Supplier</th>
            <th className="px-4 py-2.5">Storeroom</th><th className="px-4 py-2.5 text-right">Qty</th><th className="px-4 py-2.5 text-right">Notes</th><th className="px-4 py-2.5">Status</th><th className="px-4 py-2.5">Date</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {isLoading && <tr><td colSpan={8} className="px-4 py-6 text-center text-gray-400">Loading…</td></tr>}
          {!isLoading && rows.length === 0 && <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-400">No goods receipts yet.</td></tr>}
          {rows.map((r) => (
            <tr key={r.id} onClick={() => onOpen(r.id)} className="cursor-pointer hover:bg-brand-light/40">
              <td className="whitespace-nowrap px-4 py-2.5 font-medium text-gray-800">{r.grn_number}</td>
              <td className="whitespace-nowrap px-4 py-2.5 text-sm text-gray-600">{r.po_number}</td>
              <td className="px-4 py-2.5 text-sm text-gray-700">{r.supplier_name}</td>
              <td className="px-4 py-2.5 text-sm text-gray-600">{r.storeroom_name || '—'}</td>
              <td className="px-4 py-2.5 text-right tabular-nums text-gray-700">{num(r.qty_total)}</td>
              <td className="px-4 py-2.5 text-right tabular-nums text-gray-600">{Number(r.photo_count) || '—'}</td>
              <td className="px-4 py-2.5"><Pill status={r.status} map={GRN_PILL} /></td>
              <td className="whitespace-nowrap px-4 py-2.5 text-sm text-gray-500">{fmtDate(r.confirmed_at || r.created_at)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Receiving() {
  const [tab, setTab] = useState('expected');
  const [receivePo, setReceivePo] = useState(null);
  const [openGrn, setOpenGrn] = useState(null);

  return (
    <div className="p-6">
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-gray-900">Goods Receiving</h1>
        <p className="mt-1 text-sm text-gray-500">Book in deliveries against your purchase orders and bring the stock into inventory.</p>
      </div>

      <div className="mb-4 flex gap-1 border-b border-gray-200">
        {[['expected', 'Expected deliveries'], ['history', 'Receipt history']].map(([k, lbl]) => (
          <button key={k} type="button" onClick={() => setTab(k)}
            className={`-mb-px border-b-2 px-4 py-2 text-sm font-medium ${tab === k ? 'border-brand text-brand' : 'border-transparent text-gray-500 hover:text-gray-700'}`}>
            {lbl}
          </button>
        ))}
      </div>

      {tab === 'expected' ? <ExpectedTab onReceive={setReceivePo} /> : <HistoryTab onOpen={setOpenGrn} />}

      {receivePo && <ReceiveDrawer po={receivePo} onClose={() => setReceivePo(null)} />}
      {openGrn && <ReceiptDrawer grnId={openGrn} onClose={() => setOpenGrn(null)} />}
    </div>
  );
}

export default Receiving;
